import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useVehicle, vehicle as vehicleStore } from "@/lib/vehicle";
import { Menu, X, ShoppingBag, User } from "lucide-react";

const NAV = [
    ["Shop", "/shop"],
    ["Vehicle-Type", "/collections"],
    ["Bookings", "/bookings"],
    ["Blog", "/blog"],
    ["About", "/about"],
    ["Contact", "/contact"],
];

export function Header() {
    const items = useCart();
    const count = items.reduce((s, i) => s + i.qty, 0);
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const v = useVehicle();

    return (
        <header className="sticky top-0 z-40 border-b-2 border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--dust-50))]">
            {v && (
                <div className="border-b border-[hsl(var(--primary))]/30 bg-[hsl(var(--ink))]/95" data-testid="vehicle-context-chip">
                    <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-1.5 font-mono-brand text-[11px] uppercase tracking-wider text-[hsl(var(--primary))] sm:px-6">
                        <span>
                            <span className="text-[hsl(var(--dust-100))]">Shopping for</span>{" "}
                            <Link to={`/collections/${v.slug}`} className="font-bold text-[hsl(var(--primary))] underline underline-offset-4 hover:text-[hsl(var(--dust-50))]">{v.name}</Link>
                            {v.tagline && <span className="ml-3 hidden text-[hsl(var(--dust-100))] md:inline">· {v.tagline}</span>}
                        </span>
                        <button
                            onClick={() => vehicleStore.clear()}
                            className="text-[hsl(var(--dust-100))] hover:text-[hsl(var(--primary))]"
                            data-testid="vehicle-context-clear"
                        >
                            Clear ×
                        </button>
                    </div>
                </div>
            )}
            <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:h-24 sm:px-6">
                <Link to="/" className="flex shrink-0 items-center gap-3" data-testid="header-logo">
                    <img src="/assets/logo.png" alt="Urban Off-Road" className="h-14 w-auto sm:h-16" />
                    <span className="hidden font-display text-2xl uppercase italic leading-none sm:inline sm:text-3xl">
                        Urban Off-Road
                    </span>
                </Link>

                <nav className="hidden items-center gap-6 lg:flex">
                    {NAV.map(([label, to]) => {
                        const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
                        return (
                            <Link
                                key={to}
                                to={to}
                                className={`font-heading text-sm font-bold uppercase tracking-wider transition-colors ${active ? "text-[hsl(var(--primary))] underline underline-offset-8 decoration-4" : "text-[hsl(var(--dust-50))] hover:text-[hsl(var(--primary))]"}`}
                                data-testid={`nav-${label.toLowerCase().replace(/\s|-/g, "")}`}
                            >
                                {label}
                            </Link>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-2">
                    {user ? (
                        <>
                            {user.role === "admin" && (
                                <Link to="/admin" className="hidden font-heading text-xs font-bold uppercase tracking-wider text-[hsl(var(--primary))] underline underline-offset-4 md:inline" data-testid="header-admin-link">
                                    Admin
                                </Link>
                            )}
                            <Link
                                to="/account"
                                className="hidden items-center gap-2 border border-[hsl(var(--dust-50))]/40 px-3 py-2 font-heading text-xs uppercase tracking-wider hover:border-[hsl(var(--primary))] md:inline-flex"
                                data-testid="header-account"
                            >
                                <User className="h-4 w-4" /> {user.name?.split(" ")[0] || "You"}
                            </Link>
                            <button
                                onClick={logout}
                                className="hidden font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--dust-100))] hover:text-[hsl(var(--primary))] md:inline"
                                data-testid="header-logout"
                            >
                                Sign out
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/auth"
                            className="hidden font-heading text-xs font-bold uppercase tracking-wider text-[hsl(var(--dust-50))] hover:text-[hsl(var(--primary))] md:inline"
                            data-testid="header-signin"
                        >
                            Sign in
                        </Link>
                    )}

                    <Link
                        to="/cart"
                        className="relative inline-flex items-center gap-2 border-2 border-[hsl(var(--ink))] bg-[hsl(var(--primary))] px-3 py-2 font-heading text-sm font-bold uppercase tracking-wider text-[hsl(var(--ink))] transition-transform hover:-translate-y-0.5"
                        data-testid="header-cart"
                    >
                        <ShoppingBag className="h-4 w-4" />
                        <span className="hidden sm:inline">Cart</span>
                        <span className="font-mono-brand text-xs" data-testid="cart-count">[{count}]</span>
                    </Link>

                    <button
                        onClick={() => setOpen((o) => !o)}
                        className="lg:hidden border border-[hsl(var(--dust-50))]/30 p-2"
                        data-testid="header-mobile-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <nav className="lg:hidden border-t border-[hsl(var(--dust-50))]/20 bg-[hsl(var(--ink))] px-4 py-4">
                    <ul className="space-y-3">
                        {NAV.map(([label, to]) => (
                            <li key={to}>
                                <Link
                                    to={to}
                                    className="font-heading text-sm font-bold uppercase tracking-wider text-[hsl(var(--dust-50))] hover:text-[hsl(var(--primary))]"
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${label.toLowerCase().replace(/\s|-/g, "")}`}
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                        {user && user.role === "admin" && (
                            <li><Link to="/admin" className="font-heading text-sm text-[hsl(var(--primary))]" onClick={() => setOpen(false)}>Admin</Link></li>
                        )}
                        {user ? (
                            <>
                                <li><Link to="/account" className="font-heading text-sm text-[hsl(var(--dust-50))]" onClick={() => setOpen(false)}>Account</Link></li>
                                <li><button onClick={() => { logout(); setOpen(false); }} className="font-heading text-sm text-[hsl(var(--dust-100))]">Sign out</button></li>
                            </>
                        ) : (
                            <li><Link to="/auth" className="font-heading text-sm text-[hsl(var(--dust-50))]" onClick={() => setOpen(false)}>Sign in</Link></li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}

export function Footer() {
    return (
        <footer className="mt-24 border-t-2 border-[hsl(var(--ink))] bg-[hsl(var(--ink))] text-[hsl(var(--dust-50))]">
            <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-4">
                <div>
                    <div className="font-display text-3xl uppercase italic">Urban Off-Road</div>
                    <p className="mt-3 max-w-xs text-sm text-[hsl(var(--dust-100))]">
                        Gear built for the trail, the desert, and the one wrong turn that turned out to be the best part.
                    </p>
                </div>
                <div>
                    <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--primary))]">Shop</div>
                    <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--dust-100))]">
                        {["Lighting", "Recovery", "Storage", "Armor"].map((l) => (
                            <li key={l}><Link className="hover:text-[hsl(var(--dust-50))]" to={`/shop?category=${encodeURIComponent(l)}`}>{l}</Link></li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--primary))]">Support</div>
                    <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--dust-100))]">
                        <li><Link className="hover:text-[hsl(var(--dust-50))]" to="/contact">Contact</Link></li>
                        <li><Link className="hover:text-[hsl(var(--dust-50))]" to="/bookings">Fitment consult</Link></li>
                        <li><Link className="hover:text-[hsl(var(--dust-50))]" to="/about">About</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--primary))]">Company</div>
                    <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--dust-100))]">
                        <li><Link className="hover:text-[hsl(var(--dust-50))]" to="/blog">Journal</Link></li>
                        <li><Link className="hover:text-[hsl(var(--dust-50))]" to="/collections">By vehicle</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10 px-4 py-5 text-center font-mono-brand text-xs uppercase tracking-wider text-[hsl(var(--dust-100))]">
                © {new Date().getFullYear()} Urban Off-Road · Built for the trail
            </div>
        </footer>
    );
}
